import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useSponsorshipTiers, type SponsorshipTier } from '../hooks/useSponsorshipTiers';

const COLOR_PRESETS = [
  { label: 'Amber', value: 'bg-amber-100 text-amber-800 border-amber-200' },
  { label: 'Sky', value: 'bg-sky-100 text-sky-800 border-sky-200' },
  { label: 'Slate', value: 'bg-slate-100 text-slate-700 border-slate-200' },
  { label: 'Yellow', value: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { label: 'Gray', value: 'bg-gray-100 text-gray-600 border-gray-200' },
  { label: 'Orange', value: 'bg-orange-100 text-orange-700 border-orange-200' },
  { label: 'Green', value: 'bg-green-100 text-green-800 border-green-200' },
  { label: 'Blue', value: 'bg-blue-100 text-blue-800 border-blue-200' },
  { label: 'Red', value: 'bg-red-100 text-red-700 border-red-200' },
  { label: 'Teal', value: 'bg-teal-100 text-teal-800 border-teal-200' },
];

interface TierFormData {
  name: string;
  price_label: string;
  min_contribution: number;
  color_class: string;
  features: string[];
  is_category_tier: boolean;
  is_active: boolean;
  display_order: number;
}

const emptyForm: TierFormData = {
  name: '',
  price_label: '',
  min_contribution: 0,
  color_class: 'bg-gray-100 text-gray-600 border-gray-200',
  features: [''],
  is_category_tier: false,
  is_active: true,
  display_order: 99,
};

export default function SponsorshipTiers() {
  const { tiers, loading, usingFallback, createTier, updateTier, deleteTier } = useSponsorshipTiers(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<SponsorshipTier | null>(null);
  const [form, setForm] = useState<TierFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SponsorshipTier | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'standard' | 'category'>('standard');

  const standardTiers = tiers.filter(t => !t.is_category_tier);
  const categoryTiers = tiers.filter(t => t.is_category_tier);

  const openCreate = () => {
    setEditingTier(null);
    setForm({
      ...emptyForm,
      is_category_tier: activeTab === 'category',
      display_order: tiers.length + 1,
    });
    setModalOpen(true);
  };

  const openEdit = (tier: SponsorshipTier) => {
    setEditingTier(tier);
    setForm({
      name: tier.name,
      price_label: tier.price_label,
      min_contribution: tier.min_contribution,
      color_class: tier.color_class,
      features: tier.features.length > 0 ? tier.features : [''],
      is_category_tier: tier.is_category_tier,
      is_active: tier.is_active,
      display_order: tier.display_order,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = {
      ...form,
      features: form.features.filter(f => f.trim() !== ''),
    };
    let ok: boolean;
    if (editingTier) {
      ok = await updateTier(editingTier.id, payload);
    } else {
      ok = await createTier(payload);
    }
    setSaving(false);
    if (ok) setModalOpen(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await deleteTier(deleteTarget.id);
    setDeleting(false);
    setDeleteTarget(null);
  };

  const setFeature = (index: number, value: string) => {
    const updated = [...form.features];
    updated[index] = value;
    setForm(f => ({ ...f, features: updated }));
  };

  const addFeature = () => setForm(f => ({ ...f, features: [...f.features, ''] }));

  const removeFeature = (index: number) =>
    setForm(f => ({ ...f, features: f.features.filter((_, i) => i !== index) }));

  const moveFeature = (index: number, dir: -1 | 1) => {
    const updated = [...form.features];
    const swapIdx = index + dir;
    if (swapIdx < 0 || swapIdx >= updated.length) return;
    [updated[index], updated[swapIdx]] = [updated[swapIdx], updated[index]];
    setForm(f => ({ ...f, features: updated }));
  };

  const displayedTiers = activeTab === 'standard' ? standardTiers : categoryTiers;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-golf-gray">Sponsorship Tiers</h2>
          <p className="text-sm text-gray-500 mt-1">Manage tier names, pricing, benefits, and styling</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-golf-orange text-white rounded-lg font-medium hover:bg-golf-orange/90 transition-colors text-sm"
        >
          <PlusIcon className="w-4 h-4" />
          Add Tier
        </button>
      </div>

      {usingFallback && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          <InformationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Database table not found</p>
            <p className="mt-0.5 text-amber-700">Showing built-in defaults. Run the migration in your Supabase SQL Editor to enable full management. See the SQL below.</p>
            <details className="mt-2">
              <summary className="cursor-pointer font-medium hover:underline">Show migration SQL</summary>
              <pre className="mt-2 bg-amber-100 rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap">
{`CREATE TABLE IF NOT EXISTS sponsorship_tiers (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  name text UNIQUE NOT NULL,
  display_order integer NOT NULL DEFAULT 99,
  price_label text NOT NULL DEFAULT '',
  min_contribution bigint NOT NULL DEFAULT 0,
  color_class text NOT NULL DEFAULT 'bg-gray-100 text-gray-600 border-gray-200',
  features text[] NOT NULL DEFAULT '{}',
  is_category_tier boolean NOT NULL DEFAULT false,
  is_active boolean NOT NULL DEFAULT true,
  created_at timestamptz DEFAULT now(),
  updated_at timestamptz DEFAULT now()
);
ALTER TABLE sponsorship_tiers ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Anyone can read active sponsorship tiers"
  ON sponsorship_tiers FOR SELECT USING (is_active = true);
CREATE POLICY "Authenticated users can insert sponsorship tiers"
  ON sponsorship_tiers FOR INSERT TO authenticated WITH CHECK (true);
CREATE POLICY "Authenticated users can update sponsorship tiers"
  ON sponsorship_tiers FOR UPDATE TO authenticated USING (true) WITH CHECK (true);
CREATE POLICY "Authenticated users can delete sponsorship tiers"
  ON sponsorship_tiers FOR DELETE TO authenticated USING (true);`}
              </pre>
            </details>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          {(['standard', 'category'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'border-b-2 border-golf-orange text-golf-orange'
                  : 'text-gray-500 hover:text-golf-gray'
              }`}
            >
              {tab === 'standard' ? 'Standard Tiers' : 'Category Tiers'}
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab ? 'bg-golf-orange/10 text-golf-orange' : 'bg-gray-100 text-gray-500'
              }`}>
                {tab === 'standard' ? standardTiers.length : categoryTiers.length}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
          </div>
        ) : displayedTiers.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <p className="text-sm">No tiers yet. Click "Add Tier" to create one.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {displayedTiers.map(tier => (
              <motion.div
                key={tier.id}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-4 p-5 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex flex-col items-center gap-0.5 text-gray-300 flex-shrink-0">
                    <span className="text-xs font-mono text-gray-400">{tier.display_order}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${tier.color_class}`}>
                        {tier.name}
                      </span>
                      {!tier.is_active && (
                        <span className="text-xs bg-red-50 text-red-500 border border-red-100 px-2 py-0.5 rounded-full">Inactive</span>
                      )}
                      <span className="text-sm font-semibold text-golf-gray">{tier.price_label}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {tier.features.slice(0, 4).map((f, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {f}
                        </span>
                      ))}
                      {tier.features.length > 4 && (
                        <span className="text-xs text-gray-400">+{tier.features.length - 4} more</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => openEdit(tier)}
                    className="p-1.5 text-gray-400 hover:text-golf-orange hover:bg-golf-orange/10 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(tier)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50"
              onClick={() => !saving && setModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                <h3 className="text-lg font-semibold text-golf-gray">
                  {editingTier ? 'Edit Tier' : 'New Tier'}
                </h3>
                <button
                  onClick={() => setModalOpen(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tier Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange"
                      placeholder="e.g. Diamond"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price Label</label>
                    <input
                      type="text"
                      value={form.price_label}
                      onChange={e => setForm(f => ({ ...f, price_label: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange"
                      placeholder="e.g. ₦26M+"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Min. Contribution (₦)</label>
                    <input
                      type="number"
                      value={form.min_contribution}
                      onChange={e => setForm(f => ({ ...f, min_contribution: Number(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange"
                      min={0}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                    <input
                      type="number"
                      value={form.display_order}
                      onChange={e => setForm(f => ({ ...f, display_order: Number(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange"
                      min={1}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Badge Color</label>
                    <div className="flex flex-wrap gap-2">
                      {COLOR_PRESETS.map(preset => (
                        <button
                          key={preset.value}
                          type="button"
                          onClick={() => setForm(f => ({ ...f, color_class: preset.value }))}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${preset.value} ${
                            form.color_class === preset.value ? 'ring-2 ring-offset-1 ring-golf-orange' : ''
                          }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    <div className="mt-2">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${form.color_class}`}>
                        {form.name || 'Preview'}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Benefits / Features</label>
                    <button
                      type="button"
                      onClick={addFeature}
                      className="text-xs text-golf-orange hover:underline flex items-center gap-1"
                    >
                      <PlusIcon className="w-3 h-3" /> Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {form.features.map((feat, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="flex flex-col gap-0.5">
                          <button type="button" onClick={() => moveFeature(i, -1)} className="text-gray-300 hover:text-gray-500">
                            <ChevronUpIcon className="w-3 h-3" />
                          </button>
                          <button type="button" onClick={() => moveFeature(i, 1)} className="text-gray-300 hover:text-gray-500">
                            <ChevronDownIcon className="w-3 h-3" />
                          </button>
                        </div>
                        <input
                          type="text"
                          value={feat}
                          onChange={e => setFeature(i, e.target.value)}
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange"
                          placeholder={`Benefit ${i + 1}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeFeature(i)}
                          className="text-gray-300 hover:text-red-400 transition-colors"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_category_tier}
                      onChange={e => setForm(f => ({ ...f, is_category_tier: e.target.checked }))}
                      className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
                    />
                    <span className="text-sm text-gray-700">Category tier (e.g. Official Airline)</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                      className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3">
                <button
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !form.name.trim()}
                  className="px-4 py-2 text-sm font-medium bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <span className="w-3 h-3 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-4 h-4" />
                      {editingTier ? 'Save Changes' : 'Create Tier'}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50"
              onClick={() => !deleting && setDeleteTarget(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6"
            >
              <div className="flex items-center justify-center w-12 h-12 bg-red-50 rounded-full mx-auto mb-4">
                <XCircleIcon className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-golf-gray text-center mb-2">Delete Tier?</h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 text-sm font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <span className="w-3 h-3 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                  ) : 'Delete'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
